// Zvýrazněné recenze na detailu produktu (recenze U3, přepínač `reviewHighlights`).
//
// Rozhodnutí Tomáše 6. 10. 2026 (plán ~/.claude/plans/recenze-web.md, plátno
// docs/design/2026-10-06-recenze-detail/, varianta A + D):
//   A  karta pod galerií (desktop), na mobilu a tabletu (≤ lg, sloupce pod
//      sebou) za nákupní kartou jako vodorovný pás karet;
//   D  citát z recenze pod meta řádkem hlavičky (u názvu, nad ohybem).
//
// Patra obsahu (`/specs?reviews=<kód>` → `reviews`, denní job recenzí na s1,
// search/indexer/reviews.mjs):
//   1. vlastní recenze produktu 4–5★ s textem (`top`, od 7. 10. bez prahu
//      délky) → „Recenze zákazníků" + D;
//   2. produkt bez citovatelné vlastní recenze, ale s hodnocením obchodu od
//      zákazníků, kteří ho koupili (`buyers`) → „Zákazníci, kteří koupili tento
//      produkt" s poctivým štítkem, bez D;
//   3. (přepínač `reviewHighlightsShop`, Tomáš 7. 10.) produkt bez obojího →
//      „Hodnocení obchodu Jipos.cz" s obecnými 5★ citacemi obchodu, bez D.
// S týmž přepínačem karta s jedinou citací doplní druhé místo (řada desktopu):
// vlastní recenze → hodnocení od kupujícího → hodnocení obchodu, každá karta se
// svým štítkem. Citace obchodu i jeho skóre jsou data patičky (reviews.js
// `loadReviews`, `/reviews/context?page=obecne` a `/reviews/summary`), žádný
// další dotaz; karta bere ty, které patička neukazuje (`shopQuotes`).
// Recenze obchodu nedostávají schema markup (vydává ho jen nativní panel
// produktu), histogram nativního panelu zůstává úplný.
//
// Počet a průměr v hlavičce karty jsou NATIVNÍ (DOM detailu), ať sedí s panelem
// hodnocení; z indexu jde jen „Všech N recenzí" (`stats.text_count`) a citace.
// Texty doslova, bez jmen (job je do souboru nepustí).

import { icon } from './icon.js';
import { number } from './money.js';
import { page } from './pages.js';
import { quoteText } from './reviewsModel.js';
import { verifyHref } from './reviewsVerify.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/reviewHighlights.js';

/** Kolik karet kupujících se kreslí (desktop mřížka 2 sloupce, pás na mobilu stejně). */
export const BUYER_CARDS = 2;

/** Karet v řadě desktopu: výplň doplňuje do tohoto počtu, patro 3 jich kreslí tolik. */
export const CARDS = 2;

/** Vlastních recenzí se kreslí všechny z indexu (≤ 4); desktop CSS ukáže první dvě. */
const OWN_CARDS = 4;

/** Odkaz na celou sekci hodnocení produktu. */
const RATING_HREF = '#ratingTab';

function node(tag, className, content) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (content != null) el.textContent = content;
  return el;
}

function link(href, className, content) {
  const a = node('a', className, content);
  a.setAttribute('href', href);
  return a;
}

/** „4,5": jedno desetinné místo s čárkou (cs i sk). */
export function decimal(value) {
  return (Math.round(value * 10) / 10).toFixed(1).replace('.', ',');
}

/** „2025-11-10" → „10. 11. 2025"; jiný tvar = null. */
export function formatDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? Number(m[3]) + '. ' + Number(m[2]) + '. ' + m[1] : null;
}

/**
 * Co se kreslí. `reviews` = pole z `/specs` (null = produkt v souboru recenzí
 * není), `native` = {count, avg, share} z DOM detailu, `fill` = `{shop, quotes}`
 * s přepínačem `reviewHighlightsShop` (hodnocení obchodu celkem a citace obchodu
 * ze `shopQuotes`), jinak null. Vrací null (nic), nebo popis karty; citace nesou
 * `tier` ('own' | 'buyer' | 'shop'), podle něj má karta štítek:
 *   {kind: 'own', quotes, textCount, count, avg, share}
 *   {kind: 'buyers', quotes, more, firstReview, writeReview, shop}
 *   {kind: 'shop', quotes, firstReview, writeReview, shop}
 *
 * `firstReview` = produkt nemá ani jedno hodnocení (věta „Recenzi produktu zatím
 * nikdo nenapsal" a odkaz na první recenzi); `writeReview` = jen hvězdičky bez
 * textu (2025 jich přibylo 1 432): vedle „90 hodnocení" v hlavičce by „nikdo
 * nenapsal" a „první recenze" vypadaly jako lež (BT7010, Tomáš 6. 10.).
 */
export function highlightPlan(reviews, native, fill) {
  const r = reviews && typeof reviews === 'object' ? reviews : {};
  const nat = native || {};
  const rated = nat.count > 0;
  const textCount = Math.max(0, Number(r.stats && r.stats.text_count) || 0);
  const top = (Array.isArray(r.top) ? r.top : []).filter(isQuote);
  const buyers = r.buyers && typeof r.buyers === 'object' ? r.buyers : null;
  const buyerItems = (buyers && Array.isArray(buyers.items) ? buyers.items : []).filter(isQuote);
  const extra = fill && Array.isArray(fill.quotes) ? fill.quotes.filter(isQuote) : [];
  const shop = shopScore(r.shop) || (fill ? shopScore(fill.shop) : null);
  // vlastní recenze jen tam, kde je ukazuje i nativní panel (index je o den starší
  // než stránka: recenze mezitím skrytá = panel prázdný, karta by odkazovala do prázdna)
  if (top.length && rated) {
    const quotes = tagged(top.slice(0, OWN_CARDS), 'own');
    if (fill) topUp(quotes, tagged(buyerItems, 'buyer').concat(tagged(extra, 'shop')));
    return { kind: 'own', quotes, textCount, count: nat.count, avg: nat.avg || 0, share: nat.share };
  }
  const unrated = textCount === 0 && !rated;
  if (buyerItems.length) {
    const quotes = tagged(buyerItems.slice(0, BUYER_CARDS), 'buyer');
    const total = Math.max(quotes.length, Number(buyers.count) || 0);
    const more = total - quotes.length;
    if (fill) topUp(quotes, tagged(extra, 'shop'));
    return { kind: 'buyers', quotes, more, firstReview: unrated, writeReview: textCount === 0 && !unrated, shop };
  }
  if (!extra.length) return null;
  return { kind: 'shop', quotes: tagged(extra.slice(0, CARDS), 'shop'), firstReview: !rated, writeReview: rated, shop };
}

/**
 * Má smysl na citace obchodu počkat? Ne, když vlastní recenze a hodnocení od
 * kupujících samy zaplní řadu: karta pak stojí hned s `/specs` jako dřív.
 */
export function needsShopQuotes(reviews, native) {
  const plan = highlightPlan(reviews, native, { shop: null, quotes: [] });
  return !plan || plan.quotes.length < CARDS;
}

/**
 * Citace obchodu pro kartu (patro 3 a výplň): obecné 5★ citace z `/reviews/context`
 * (tytéž, ze kterých patička bere tři) bez těch, které ukazuje patička, a bez
 * opakování. Pořadí začíná na pozici podle kódu produktu: různé produkty mají
 * různé citace, týž produkt stejné, dokud se seznam nezmění (denní job).
 */
export function shopQuotes(items, footer, code) {
  const taken = new Set((footer || []).map(quoteText));
  const list = (Array.isArray(items) ? items : []).filter(function (q) {
    if (!isQuote(q)) return false;
    const key = quoteText(q);
    if (taken.has(key)) return false;
    taken.add(key);
    return true;
  });
  if (!list.length) return [];
  const start = seed(String(code || '')) % list.length;
  return list.slice(start).concat(list.slice(0, start));
}

/** Stabilní číslo z řetězce (kód produktu → začátek výběru citací). */
function seed(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function shopScore(s) {
  return s && Number(s.count) > 0 ? { count: Number(s.count), avg: Number(s.avg) || 0 } : null;
}

function tagged(list, tier) {
  return list.map(function (q) {
    return Object.assign({}, q, { tier: tier });
  });
}

/** Doplní `quotes` z `pool` do počtu CARDS; shodný text (citace kupujícího bývá i v obecných) jen jednou. */
function topUp(quotes, pool) {
  const seen = new Set(quotes.map(quoteText));
  for (let i = 0; i < pool.length && quotes.length < CARDS; i++) {
    const key = quoteText(pool[i]);
    if (seen.has(key)) continue;
    seen.add(key);
    quotes.push(pool[i]);
  }
}

function isQuote(q) {
  return !!q && typeof q === 'object' && (hasText(q.text) || (Array.isArray(q.pros) && q.pros.some(hasText)));
}

function hasText(s) {
  return typeof s === 'string' && s.trim() !== '';
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

/** Hvězdy s částečným vyplněním (sdílený primitiv `.bs-stars`, _stars.scss). */
function stars(value, className) {
  const wrap = node('span', 'bs-stars ' + className, '★★★★★');
  wrap.setAttribute('aria-hidden', 'true');
  const fill = node('span', 'bs-stars__fill', '★★★★★');
  wrap.style.setProperty('--bs-stars-pct', Math.round((value / 5) * 1000) / 10 + '%');
  wrap.appendChild(fill);
  return wrap;
}

/** Hvězdy recenze: plné glyfy + šedé zbylé (jako v návrhu, bez překryvu). */
function quoteStars(rating) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  const el = node('span', 'bs-rh__stars', '★'.repeat(n));
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', L.starsAria(n));
  if (n < 5) el.appendChild(node('span', 'bs-rh__stars-off', '★'.repeat(5 - n)));
  return el;
}

function sourceLabel(q) {
  const heureka = q.source === 'heureka';
  if (q.tier === 'buyer' || q.tier === 'shop') {
    const el = node('span', 'bs-rh__source' + (heureka ? '' : ' bs-rh__source--plain'));
    if (heureka) el.appendChild(icon('shield'));
    const label = q.tier === 'shop' ? (heureka ? L.shopHeureka : L.shopLabel) : heureka ? L.buyerHeureka : L.buyer;
    el.appendChild(node('span', null, label));
    return el;
  }
  if (heureka) {
    const el = node('span', 'bs-rh__source');
    el.appendChild(icon('shield'));
    el.appendChild(node('span', null, L.sourceHeureka));
    return el;
  }
  // `web` i neznámý původ: neověřeno, šedý štítek bez ikony
  return node('span', 'bs-rh__source bs-rh__source--plain', L.sourceWeb(SHOP.shop.name));
}

/** Text citace: body Heureky jako řádky („+ …", „− …"), jinak odstavec. */
function quoteBody(q) {
  const text = hasText(q.text) ? q.text.trim() : '';
  // bod, který text recenze už obsahuje, se neopakuje (Heureka ho často
  // přepisuje do souhrnu: „… Celková spokojenost" + bod „Celková spokojenost")
  const fresh = function (s) {
    return hasText(s) && text.toLowerCase().indexOf(s.trim().toLowerCase()) < 0;
  };
  const pros = (q.pros || []).filter(fresh);
  const cons = (q.cons || []).filter(fresh);
  if (!pros.length && !cons.length) return node('p', 'bs-rh__text', text);
  const body = node('div', 'bs-rh__text bs-rh__text--points');
  pros.forEach(function (p) {
    const line = node('span', 'bs-rh__point');
    line.appendChild(node('b', 'bs-rh__plus', '+'));
    line.appendChild(document.createTextNode(' ' + p.trim()));
    body.appendChild(line);
  });
  cons.forEach(function (c) {
    const line = node('span', 'bs-rh__point');
    line.appendChild(node('b', 'bs-rh__minus', '\u2212'));
    line.appendChild(document.createTextNode(' ' + c.trim()));
    body.appendChild(line);
  });
  if (text) body.appendChild(node('span', 'bs-rh__point', text));
  return body;
}

function quoteCard(q) {
  const card = node('article', 'bs-rh__card');
  if (q.tier) card.dataset.bsTier = q.tier;
  const head = node('div', 'bs-rh__card-head');
  head.appendChild(quoteStars(q.rating));
  const date = formatDate(q.date);
  if (date) head.appendChild(node('span', 'bs-rh__date', date));
  card.appendChild(head);
  card.appendChild(quoteBody(q));
  card.appendChild(sourceLabel(q));
  return card;
}

function cta(href, label) {
  const a = link(href, 'bs-rh__cta');
  a.appendChild(node('span', null, label));
  a.appendChild(icon('arrow-right'));
  return a;
}

/** Odkaz na sekci „Jak recenze ověřujeme" stránky hodnocení, nebo null (shop ji nemá). */
function verifyLink(className, label) {
  const href = verifyHref();
  return href ? link(href, className, label) : null;
}

function ratingsText(count) {
  return L.ratings(number(count), count);
}

function buildOwn(plan) {
  const sec = node('section', 'bs-rh');
  sec.setAttribute('aria-label', L.title);

  const head = node('div', 'bs-rh__head');
  head.appendChild(node('h2', 'bs-rh__title', L.title));
  const score = node('span', 'bs-rh__score');
  score.appendChild(node('span', 'bs-rh__num', decimal(plan.avg)));
  score.appendChild(stars(plan.avg, 'bs-rh__score-stars'));
  score.appendChild(link(RATING_HREF, 'bs-rh__count', ratingsText(plan.count)));
  head.appendChild(score);
  if (plan.share !== null && plan.share !== undefined) head.appendChild(node('span', 'bs-rh__share', L.share(plan.share)));
  sec.appendChild(head);

  const list = node('div', 'bs-rh__list');
  plan.quotes.forEach(function (q) {
    list.appendChild(quoteCard(q));
  });
  sec.appendChild(list);

  const foot = node('div', 'bs-rh__foot');
  const n = plan.textCount;
  foot.appendChild(cta(RATING_HREF, n >= 2 ? L.allReviews(number(n), n) : L.allRatings));
  const verify = verifyLink('bs-rh__verify', L.verify);
  if (verify) foot.appendChild(verify);
  sec.appendChild(foot);

  // mobil: počet a podíl 4★+ až pod pásem karet (v hlavičce na ně není místo)
  const sum = node('div', 'bs-rh__summary');
  let line = ratingsText(plan.count);
  if (plan.share !== null && plan.share !== undefined) line += ' · ' + L.share(plan.share);
  sum.appendChild(node('span', null, line));
  const verifyShort = verifyLink('bs-rh__verify', L.verifyShort);
  if (verifyShort) sum.appendChild(verifyShort);
  sec.appendChild(sum);
  return sec;
}

/** Karta kupujících (patro 2) i karta obchodu (patro 3): stejná podoba, jiné texty. */
function buildBuyers(plan) {
  const shopTier = plan.kind === 'shop';
  const title = shopTier ? L.shopTitle(SHOP.shop.name) : L.buyersTitle;
  const sec = node('section', 'bs-rh bs-rh--buyers' + (shopTier ? ' bs-rh--shop' : ''));
  sec.setAttribute('aria-label', title);
  const ratingsPage = page('ratings');

  const head = node('div', 'bs-rh__head');
  const text = node('div', 'bs-rh__head-text');
  text.appendChild(node('h2', 'bs-rh__title', title));
  const sub = shopTier ? (plan.firstReview ? L.shopSubFirst : L.shopSub) : plan.firstReview ? L.buyersSubFirst : L.buyersSub;
  text.appendChild(node('span', 'bs-rh__sub', sub));
  head.appendChild(text);
  if (plan.shop) {
    const shop = ratingsPage ? link(ratingsPage, 'bs-rh__shop') : node('span', 'bs-rh__shop');
    shop.appendChild(node('span', 'bs-rh__num', decimal(plan.shop.avg)));
    const col = node('span', 'bs-rh__shop-col');
    col.appendChild(stars(plan.shop.avg, 'bs-rh__shop-stars'));
    col.appendChild(node('span', 'bs-rh__shop-count', L.shopRatings(number(plan.shop.count), plan.shop.count)));
    shop.appendChild(col);
    head.appendChild(shop);
  }
  sec.appendChild(head);

  const list = node('div', 'bs-rh__list');
  plan.quotes.forEach(function (q) {
    list.appendChild(quoteCard(q));
  });
  sec.appendChild(list);

  const foot = node('div', 'bs-rh__foot');
  if (ratingsPage) foot.appendChild(cta(ratingsPage, plan.more > 0 ? L.moreBuyers(number(plan.more), plan.more) : L.allShopRatings));
  if (plan.firstReview) foot.appendChild(link(RATING_HREF, 'bs-rh__first', L.firstReview));
  else if (plan.writeReview) foot.appendChild(link(RATING_HREF, 'bs-rh__first', L.writeReview));
  if (foot.children.length) sec.appendChild(foot);
  return sec;
}

/** Citát D pod meta řádkem hlavičky (jen vlastní recenze). */
function buildQuote(plan) {
  const q = plan.quotes[0];
  const a = link(RATING_HREF, 'bs-rh-quote');
  const mark = node('span', 'bs-rh-quote__mark', '\u201E');
  mark.setAttribute('aria-hidden', 'true');
  a.appendChild(mark);
  const body = node('span', 'bs-rh-quote__body');
  const pros = (q.pros || []).filter(hasText);
  body.appendChild(node('span', 'bs-rh-quote__text', hasText(q.text) ? q.text.trim() : pros.join(' · ')));
  const meta = node('span', 'bs-rh-quote__meta');
  const parts = [];
  if (q.source === 'heureka') parts.push(L.sourceHeureka);
  const date = formatDate(q.date);
  if (date) parts.push(date);
  if (parts.length) meta.appendChild(document.createTextNode(parts.join(' · ')));
  const rest = plan.textCount - 1;
  if (rest > 0) {
    if (parts.length) meta.appendChild(document.createTextNode(' · '));
    const more = node('b', 'bs-rh-quote__more', L.more(number(rest), rest));
    // „recenzí" na mobilu odpadá (návrh: „dalších 103")
    more.appendChild(node('span', 'bs-rh-quote__noun', ' ' + L.moreNoun(rest)));
    meta.appendChild(more);
  }
  if (meta.childNodes.length) body.appendChild(meta);
  a.appendChild(body);
  return a;
}

// Karta stojí pod galerií, dokud jsou sloupce vedle sebe; pod sebou (≤ $bp-lg,
// _product-detail.scss) jde za nákupní kartu, jinak by na mobilu předběhla cenu.
const STACKED = '(max-width: 900px)';

function place(sec, ctx) {
  const stacked = window.matchMedia && window.matchMedia(STACKED).matches;
  if (!stacked && ctx.gallery) {
    ctx.gallery.appendChild(sec);
    return;
  }
  const data = ctx.data;
  if (!data) return;
  const advisor = data.querySelector(':scope > .bs-advisor');
  if (advisor) data.insertBefore(sec, advisor);
  else data.appendChild(sec);
}

/**
 * Vykreslí kartu (a citát) z pole `reviews` položky `/specs`. `ctx` =
 * `{gallery, data, head, native: {count, avg, share}, fill?}` (`fill` viz
 * `highlightPlan`). Idempotentní.
 */
export function mountReviewHighlights(reviews, ctx) {
  if (!ctx || document.querySelector('.bs-rh')) return null;
  const plan = highlightPlan(reviews, ctx.native, ctx.fill || null);
  if (!plan) return null;
  const sec = plan.kind === 'own' ? buildOwn(plan) : buildBuyers(plan);
  place(sec, ctx);
  if (window.matchMedia) {
    const mq = window.matchMedia(STACKED);
    const move = function () {
      place(sec, ctx);
    };
    if (mq.addEventListener) mq.addEventListener('change', move);
  }
  if (plan.kind === 'own' && ctx.head) {
    const meta = ctx.head.querySelector('.bs-pd__meta');
    const quote = buildQuote(plan);
    if (meta) meta.insertAdjacentElement('afterend', quote);
    else ctx.head.appendChild(quote);
  }
  document.documentElement.classList.add('bs-rh-ready');
  return plan;
}
